<template>
  <RevStatusTracker
    :current-step
    orientation="vertical"
    progress-bar-color="success"
    start-with="last"
    :steps="mappedSteps"
  >
    <template v-for="step in steps" :key="step.key" #[`${step.key}-content`]>
      <div class="flex text-action-default-low">
        <p class="caption grow">
          <FormattedMessage
            v-if="step.date"
            class="block"
            :definition="translations.stepDatetime"
          >
            <template #date>
              <SafeTime
                :datetime="step.date"
                :options="{ month: 'long', day: 'numeric', year: 'numeric' }"
              />
            </template>
            <template #time>
              <SafeTime
                :datetime="step.date"
                :options="{ hour: '2-digit', minute: '2-digit' }"
              />
            </template>
          </FormattedMessage>
          <template v-if="step.address">
            <template v-if="isFullAddress(step.address)">
              <span v-if="formatAddressRecipient(step.address)" class="block">
                {{ formatAddressRecipient(step.address) }}
              </span>
              <span class="block">
                {{ formatAddressWithoutRecipient(step.address) }}
              </span>
            </template>
            <template v-else>
              {{ step.address }}
            </template>
          </template>
        </p>
        <RevTooltip
          v-if="canCopyPickUpAddress(step)"
          class="shrink-0 flex items-center"
          position="top"
        >
          <template #trigger="{ show, hide }">
            <RevButtonIcon
              :aria-label="i18n(translations.copyAddress)"
              :icon="IconCopy"
              size="small"
              variant="ghost"
              @click="handleCopyAddress(step.address, show, hide)"
              @focusout="hide"
              @mouseleave="hide"
            />
          </template>
          {{ i18n(translations.addressCopied) }}
        </RevTooltip>
      </div>
    </template>
  </RevStatusTracker>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import SafeTime from '@backmarket/nuxt-layer-base/components/SafeTime/SafeTime.vue'
import type { Address } from '@backmarket/nuxt-module-address/address'
import { useFormatAddress } from '@backmarket/nuxt-module-address/useFormatAddress'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevStatusTracker } from '@ds/components/StatusTracker'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCopy } from '@ds/icons/IconCopy'

import type { Checkpoint, Step } from '~/api/tracking/types'

import translations from './DeliveryTimeline.translations'
import type { StatusTrackerStep } from './DeliveryTimeline.types'

const props = defineProps<{
  steps: StatusTrackerStep[]
  currentStep: string
}>()

const i18n = useI18n()

const STEP_LABEL_TRANSLATIONS: Record<
  Step['name'],
  (typeof translations)[keyof typeof translations]
> = {
  ORDER_CREATED: translations.stepOrderCreated,
  READY_TO_SHIP: translations.stepReadyToShip,
  DISPATCHED: translations.stepDispatched,
  IN_DELIVERY: translations.stepInDelivery,
  AVAILABLE_FOR_COLLECTION: translations.stepAvailableForCollection,
  DELIVERED: translations.stepDelivered,
  FAILED_ATTEMPT: translations.stepFailedAttempt,
  EXCEPTION: translations.stepException,
}

const CHECKPOINT_LABEL_TRANSLATIONS: Record<
  Checkpoint['name'],
  (typeof translations)[keyof typeof translations]
> = {
  ORDER_CREATED: translations.checkpointOrderCreated,
  READY_TO_SHIP: translations.checkpointReadyToShip,
  IN_TRANSIT: translations.checkpointInTransit,
  ACCEPTANCE_SCAN: translations.checkpointAcceptanceScan,
  ARRIVAL_SCAN: translations.checkpointArrivalScan,
  ARRIVED_AT_DESTINATION: translations.checkpointArrivedAtDestination,
  CUSTOMS_CLEARANCE_COMPLETED: translations.checkpointCustomsClearanceCompleted,
  CUSTOMS_CLEARANCE_STARTED: translations.checkpointCustomsClearanceStarted,
  DEPARTURE_SCAN: translations.checkpointDepartureScan,
  PROBLEM_RESOLVED: translations.checkpointProblemResolved,
  FORWARDED_TO_DIFFERENT_DELIVERY_ADDRESS:
    translations.checkpointForwardedToDifferentDeliveryAddress,
  OUT_FOR_DELIVERY: translations.checkpointOutForDelivery,
  CUSTOMER_CONTACTED: translations.checkpointCustomerContacted,
  DELIVERY_APPOINTMENT_SCHEDULED:
    translations.checkpointDeliveryAppointmentScheduled,
  AVAILABLE_FOR_PICKUP: translations.checkpointAvailableForPickup,
  DELIVERED: translations.checkpointDelivered,
  PICKED_UP_BY_CUSTOMER: translations.checkpointPickedUpByCustomer,
  SIGNED_BY_CUSTOMER: translations.checkpointSignedByCustomer,
  DELIVERED_CASH_ON_DELIVERY: translations.checkpointDeliveredCashOnDelivery,
  FAILED_ATTEMPT: translations.checkpointFailedAttempt,
  ADDRESSEE_NOT_AVAILABLE: translations.checkpointAddresseeNotAvailable,
  BUSINESS_CLOSED: translations.checkpointBusinessClosed,
  EXCEPTION: translations.checkpointException,
  CUSTOMER_MOVED: translations.checkpointCustomerMoved,
  CUSTOMER_REFUSED_DELIVERY: translations.checkpointCustomerRefusedDelivery,
  DELAYED_CUSTOMS_CLEARANCE: translations.checkpointDelayedCustomsClearance,
  DELAYED_EXTERNAL_FACTORS: translations.checkpointDelayedExternalFactors,
  HELD_FOR_PAYMENT: translations.checkpointHeldForPayment,
  INCORRECT_ADDRESS: translations.checkpointIncorrectAddress,
  PICK_UP_MISSED: translations.checkpointPickUpMissed,
  REJECTED_BY_CARRIER: translations.checkpointRejectedByCarrier,
  RETURNING_TO_SENDER: translations.checkpointReturningToSender,
  RETURNED_TO_SENDER: translations.checkpointReturnedToSender,
  SHIPMENT_DAMAGE: translations.checkpointShipmentDamage,
  SHIPMENT_LOST: translations.checkpointShipmentLost,
}

function isFullAddress(address: NonNullable<StatusTrackerStep['address']>) {
  return typeof address !== 'string'
}

function getStepLabel(step: StatusTrackerStep): string {
  // Some checkpoints have the very same name as steps.
  // As the translations are the same for now, we don't differentiate between Step and Checkpoint types.
  if (step.name in STEP_LABEL_TRANSLATIONS) {
    return i18n(STEP_LABEL_TRANSLATIONS[step.name as Step['name']])
  }

  return i18n(CHECKPOINT_LABEL_TRANSLATIONS[step.name as Checkpoint['name']])
}

const mappedSteps = computed(() =>
  props.steps.map((step) => ({
    key: step.key,
    label: getStepLabel(step),
  })),
)

function canCopyPickUpAddress(step: StatusTrackerStep) {
  const { name, address } = step

  return (
    address &&
    (name === 'AVAILABLE_FOR_COLLECTION' || name === 'AVAILABLE_FOR_PICKUP')
  )
}

function formatAddressRecipient(address: Address) {
  if (!address) return ''

  const name = [address.firstName, address.lastName].filter(Boolean).join(' ')

  return name || address.company || ''
}

function formatAddressWithoutRecipient(address: Address) {
  if (!address) return ''

  const lines = useFormatAddress(address, {
    includeName: false,
    includeCompany: false,
  })

  return lines.join(', ')
}

function formatAddressInline(address: Address) {
  if (!address) return ''

  const recipient = formatAddressRecipient(address)
  const addressWithoutRecipient = formatAddressWithoutRecipient(address)

  return recipient
    ? `${recipient}, ${addressWithoutRecipient}`
    : addressWithoutRecipient
}

async function handleCopyAddress(
  address: StatusTrackerStep['address'],
  show: () => void,
  hide: () => void,
) {
  try {
    if (address) {
      const formattedAddress = isFullAddress(address)
        ? formatAddressInline(address)
        : address
      await navigator.clipboard.writeText(formattedAddress)
      show()

      setTimeout(() => {
        hide()
      }, 1000)
    }
  } catch {
    // Do nothing
  }
}
</script>
